import { useCallback } from 'react';
import { GridElement, Connection, ElementAction } from '../types';
import { calculateDistance } from '../utils/converter';
import { GpsBounds } from '../types/presenter.types';
import { canvasToGps } from '../utils/converter';
import { getDefaultProperties } from '../utils/presenter.utils';

interface EventHandlersProps {
  svgRef: React.RefObject<SVGSVGElement>;
  selectedTool: string;
  elements: GridElement[];
  connections: Connection[];
  zoom: number;
  pan: { x: number; y: number };
  isDragging: boolean;
  isElementDragging: boolean;
  draggedElement: string | null;
  dragStart: { x: number; y: number };
  connectionStart: string | null;
  globalGpsBounds: GpsBounds;
  setElements: React.Dispatch<React.SetStateAction<GridElement[]>>;
  setConnections: React.Dispatch<React.SetStateAction<Connection[]>>;
  setSelectedElement: React.Dispatch<React.SetStateAction<GridElement | null>>;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  setIsElementDragging: React.Dispatch<React.SetStateAction<boolean>>;
  setDraggedElement: React.Dispatch<React.SetStateAction<string | null>>;
  setDragStart: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  setConnectionStart: React.Dispatch<React.SetStateAction<string | null>>;
  setPan: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
}

export const useEventHandlers = ({
  svgRef,
  selectedTool,
  elements,
  connections,
  zoom,
  pan,
  isDragging,
  isElementDragging,
  draggedElement,
  dragStart,
  connectionStart,
  globalGpsBounds,
  setElements,
  setConnections,
  setSelectedElement,
  setIsDragging,
  setIsElementDragging,
  setDraggedElement,
  setDragStart,
  setConnectionStart,
  setPan,
  setZoom
}: EventHandlersProps) => {

  /**
   * Handles clicks on the SVG canvas to add new elements
   * Only creates elements when a creation tool is selected (not select/move/connect)
   */
  const handleSVGClick = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
    // Don't create elements when using interaction tools
    if (['select', 'move', 'connect'].includes(selectedTool)) return;
    
    const svg = svgRef.current;
    if (!svg) return;

    // Convert screen coordinates to SVG coordinates, accounting for zoom and pan
    const rect = svg.getBoundingClientRect();
    const x = (event.clientX - rect.left - pan.x) / zoom;
    const y = (event.clientY - rect.top - pan.y) / zoom;

    // Convert canvas coordinates to GPS coordinates for new element using global bounds
    const gpsCoords = canvasToGps(x, y, globalGpsBounds);
    const gpsCoordinates = {
      latitude: gpsCoords.latitude,
      longitude: gpsCoords.longitude
    };

    // Create a new element at the clicked position
    const newElement: GridElement = {
      id: `e${Date.now()}`, // Generate unique ID using timestamp
      type: selectedTool as 'power-plant' | 'pole' | 'meter' | 'transformer',
      x,
      y,
      name: `${selectedTool} ${elements.length + 1}`, // Auto-generated name
      properties: getDefaultProperties(selectedTool), // Type-specific default properties
      status: "ACTIVE",
      lights: selectedTool === 'pole' ? [] : undefined, // Only poles can have lights
      gpsCoordinates, // Include calculated GPS coordinates
      action: ElementAction.TO_CREATE // Mark as new element to be created
    };

    // Add the new element and select it
    setElements(prev => [...prev, newElement]);
    setSelectedElement(newElement);
  }, [selectedTool, elements.length, zoom, pan, svgRef, globalGpsBounds, setElements, setSelectedElement]);

  /**
   * Handles mouse down events on grid elements
   * Behavior depends on the currently selected tool
   * Note: Element dragging is now handled by Leaflet markers
   */
  const handleElementMouseDown = (element: GridElement, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent canvas drag when clicking on elements
    
    if (selectedTool === 'select') {
      // Select tool: just select the element
      setSelectedElement(element);
    } else if (selectedTool === 'move') {
      // Move tool: select the element (dragging handled by Leaflet)
      setSelectedElement(element);
      console.log('🔧 Move tool selected element:', element.id, '- dragging handled by Leaflet');
    } else if (selectedTool === 'connect') {
      console.log('🔧 Connect tool clicked on element:', element.id);
      console.log('🔧 Current connectionStart:', connectionStart);
      
      // Connect tool: handle connection creation
      if (!connectionStart) {
        // First click: start a connection from this element
        console.log('🔧 Starting connection from element:', element.id);
        setConnectionStart(element.id);
        setSelectedElement(element);
      } else if (connectionStart !== element.id) {
        // Second click: complete the connection to this element
        console.log('🔧 Completing connection from', connectionStart, 'to', element.id);
        
        const fromElement = elements.find(el => el.id === connectionStart);
        const toElement = element;
        
        // Calculer la longueur réelle entre les éléments
        let calculatedLength = 100; // Default length
        if (fromElement && toElement) {
          const fromLat = fromElement.gpsCoordinates?.latitude || fromElement.y / 100000;
          const fromLng = fromElement.gpsCoordinates?.longitude || fromElement.x / 100000;
          const toLat = toElement.gpsCoordinates?.latitude || toElement.y / 100000;
          const toLng = toElement.gpsCoordinates?.longitude || toElement.x / 100000;
          
          calculatedLength = calculateDistance(fromLat, fromLng, toLat, toLng);
        }
        
        const newConnection: Connection = {
          id: `c${Date.now()}`, // Generate unique ID
          name: '3x150+70', // Default cable name
          fromId: connectionStart,
          toId: element.id,
          type: 'power-line',
          voltage: 400, // Default voltage
          resistance: 0.2, // Default resistance
          length: calculatedLength, // Longueur calculée basée sur la distance réelle
          status: 'ACTIVE',
          action: ElementAction.TO_CREATE // Mark as new connection to be created
        };
        
        console.log('🔧 Created new connection:', newConnection);
        setConnections(prev => [...prev, newConnection]);
        setConnectionStart(null); // Reset connection state
        console.log('🔧 Connection state reset');
      } else {
        console.log('🔧 Clicked same element, canceling connection');
        setConnectionStart(null); // Cancel connection if clicking same element
      }
      // Note: clicking the same element twice cancels the connection
    }
  };

  /**
   * Handles mouse down events on the canvas (not on elements)
   * Starts canvas dragging when in select mode and no element is selected
   */
  const handleMouseDown = (event: React.MouseEvent) => {
    // Only start canvas drag in select mode when nothing else is being interacted with
    if (selectedTool === 'select' && !isElementDragging) {
      setIsDragging(true);
      // Store the starting position relative to current pan
      setDragStart({ x: event.clientX - pan.x, y: event.clientY - pan.y });
    }
  };

  /**
   * Handles mouse move events for canvas panning only
   * Element dragging is now handled by Leaflet markers
   * Uses useCallback for performance optimization
   */
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (isDragging && !isElementDragging) {
      // Canvas panning: update pan offset based on mouse movement
      setPan({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y
      });
    }
    // Note: Element dragging is now handled by Leaflet markers in LeafletMap.tsx
    // This prevents conflicts and duplication between SVG and Leaflet systems
  }, [isDragging, isElementDragging, dragStart, setPan]);

  /**
   * Handles mouse up events to end canvas dragging operations
   * Element dragging is now handled by Leaflet markers
   */
  const handleMouseUp = () => {
    setIsDragging(false); // End canvas dragging
    // Note: Element dragging state is managed by Leaflet markers
  };

  /**
   * Handles mouse wheel events for zooming (with Ctrl/Cmd) and panning
   * Uses useCallback for performance optimization
   */
  const handleWheel = useCallback((event: React.WheelEvent<SVGSVGElement>) => {
    event.preventDefault(); // Prevent page scroll
    
    const svg = svgRef.current;
    if (!svg) return;

    // Get mouse position relative to SVG
    const rect = svg.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    // Check if Ctrl/Cmd key is pressed for zooming
    if (event.ctrlKey || event.metaKey) {
      // ZOOM MODE: Zoom in/out centered on mouse position
      const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1; // 10% zoom steps
      const newZoom = Math.max(0.1, Math.min(5, zoom * zoomFactor)); // Clamp zoom between 10% and 500%
      
      // Calculate the point that should remain stationary during zoom
      const zoomCenterX = (mouseX - pan.x) / zoom;
      const zoomCenterY = (mouseY - pan.y) / zoom;
      
      // Adjust pan so the zoom center stays under the mouse
      const newPan = {
        x: mouseX - zoomCenterX * newZoom,
        y: mouseY - zoomCenterY * newZoom
      };
      
      setZoom(newZoom);
      setPan(newPan);
    } else {
      // PAN MODE: Move the canvas with mouse wheel
      const scrollSpeed = 1; // Adjust for scroll sensitivity
      const deltaX = event.deltaX * scrollSpeed;
      const deltaY = event.deltaY * scrollSpeed;
      
      setPan(prev => ({
        x: prev.x - deltaX,
        y: prev.y - deltaY
      }));
    }
  }, [zoom, pan, svgRef, setPan, setZoom]);

  return {
    handleSVGClick,
    handleElementMouseDown,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel
  };
};
